import { Button, Input } from 'antd';
import { Separator } from 'components/common/Common';
import Loader from 'components/public/Loader';

import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';

import HeaderMeta from 'components/headerMeta/HeaderMeta';
import { useHistory, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { getBlogDetails } from 'services';
import { roll } from 'untils/auth';
import usePrerenderReady from 'untils/hook/usePrerenderReady';
import {
  buildAbsoluteUrl,
  buildArticleSchema,
  buildBreadcrumbSchema,
  SITE_URL,
} from 'untils/jsonLdSchemas';
import '../../assets/style/itemStyle/blog.scss';
import Footer from '../../components/layout/Footer';

import useLockBody from 'untils/hook/useLockBody';
import BlogAction from './components/BlogAction';

const shareIcons = [
  { className: 'icon-facebook', name: 'facebook' },
  { className: 'icon-twitter', name: 'twitter' },
  { className: 'icon-linkedin', name: 'linkedin' },
  { className: 'icon-link', name: 'link' },
];

const ARTICLE_TITLE_KEYS = ['SEOTitleTag', 'seoTitleTag', 'Title', 'title'];
const ARTICLE_DESCRIPTION_KEYS = ['SEODesc', 'seoDesc', 'describe'];
const ARTICLE_URL_KEYS = ['SEOUrl', 'seoUrl'];
const ARTICLE_KEYWORD_KEYS = ['SEOTags', 'seoTags', 'SeoTags', 'Tags', 'tags'];

const getFirstNonEmptyString = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];

    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
};

const normalizeTagValues = (source) => {
  if (Array.isArray(source)) {
    return source
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (item && typeof item === 'object') {
          return getFirstNonEmptyString(item, ['Name', 'name', 'Title', 'title', 'value']);
        }
        return '';
      })
      .filter(Boolean);
  }

  if (typeof source === 'string') {
    const normalizedSource = source.trim();

    if (!normalizedSource) {
      return [];
    }

    if (normalizedSource.startsWith('[')) {
      try {
        const parsedSource = JSON.parse(normalizedSource);
        const parsedValues = normalizeTagValues(parsedSource);

        if (parsedValues.length) {
          return parsedValues;
        }
      } catch {
        // Fall back to comma splitting for non-JSON tag strings.
      }
    }

    return source
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  return [];
};

const getArticleKeywords = (details) => {
  for (const key of ARTICLE_KEYWORD_KEYS) {
    const values = normalizeTagValues(details?.[key]);
    if (values.length) {
      return values;
    }
  }

  return undefined;
};

const summarizeArticleContent = (content) => {
  if (!content) return '';

  return content
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 160);
};

const buildArticleDescription = (details) => {
  if (!details) return '';

  const explicitDescription = getFirstNonEmptyString(details, ARTICLE_DESCRIPTION_KEYS);
  if (explicitDescription) return explicitDescription;

  const contentDescription = summarizeArticleContent(
    getFirstNonEmptyString(details, ['content', 'Content'])
  );
  if (contentDescription) return contentDescription;

  const fallbackKeywords = getArticleKeywords(details);
  if (fallbackKeywords?.length) {
    return fallbackKeywords.join(', ');
  }

  return '';
};

const BlogDetails = (props) => {
  const inputRef = useRef();
  const [details, setDetails] = useState({});
  const [recommendList, setRecommendList] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { markReady } = usePrerenderReady();
  const linkValue = window.location.href;
  let params = useParams();
  let history = useHistory();

  // prevent body from scrolling
  useLockBody(open);

  useEffect(() => {
    const controller = new AbortController();

    const getBlogDetailsData = async () => {
      const blogId = params.id;
      roll(0);

      if (blogId) {
        try {
          setLoading(true);
          const res = await getBlogDetails(blogId, { signal: controller.signal });
          if (res.code && res.data) {
            setDetails(res.data);
            setRecommendList(res.data.link);
          } else {
            history.push('/blog');
            markReady();
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.log(error);
            markReady();
          }
        } finally {
          setLoading(false);
        }
      } else {
        markReady();
      }
    };

    getBlogDetailsData();

    return () => {
      controller.abort();
    };
  }, [history, markReady, params.id]);

  //打开分享弹窗
  const openShareLinkHandle = () => {
    setOpen(true);
    setTimeout(() => {
      inputRef.current.select();
    }, 10);
  };

  //关闭分享弹窗
  const closeShareLinkHandle = () => {
    setOpen(false);
  };

  //copy分享链接
  const copyLinkHandle = () => {
    navigator.clipboard.writeText(linkValue);
    setOpen(false);
  };

  // 分享
  const shareHandle = (e) => {
    e.preventDefault();
    const type = e.target.name || e.target.getAttribute('name');
    const socialLink = {
      facebook: `https://www.facebook.com/dialog/share?app_id=${process.env.REACT_APP_FACEBOOK_APP_ID}&href=${linkValue}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(linkValue)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${linkValue}`,
    };
    const pageURL = socialLink[type] || '/';
    if (type === 'link') {
      return openShareLinkHandle();
    }
    window.open(
      `${pageURL}`,
      `${type}`,
      'height=800, width=640, top=0, left=0, toolbar=no, menubar=no, scrollbars=no,   resizable=no, location=no, status=no'
    );
  };

  const articleSlug = getFirstNonEmptyString(details, ARTICLE_URL_KEYS);
  const articlePath = articleSlug ? `/blog/${articleSlug}` : window.location.pathname;
  const pageUrl = buildAbsoluteUrl(articlePath);
  const articleTitle = getFirstNonEmptyString(details, ARTICLE_TITLE_KEYS);
  const articleDescription = buildArticleDescription(details);
  const articleImageSource = getFirstNonEmptyString(details, ['coverImage', 'CoverImage']);
  const articleImage = articleImageSource
    ? buildAbsoluteUrl(articleImageSource)
    : buildAbsoluteUrl('/imgs/webp/home/seo.jpg');
  const articleAuthor = getFirstNonEmptyString(details, ['author', 'Author']) || 'Airacer';
  const articlePublishedAt = getFirstNonEmptyString(details, ['date', 'Date']);
  const articleModifiedAt = getFirstNonEmptyString(details, ['uDate', 'UDate']);
  const articleContent = getFirstNonEmptyString(details, ['content', 'Content']);
  const articleKeywords = getArticleKeywords(details);
  const metaOverrides = articleTitle
    ? {
        title: articleTitle,
        description: articleDescription,
        keywords: articleKeywords,
        canonicalUrl: pageUrl,
        ogType: 'article',
        ogTitle: articleTitle,
        ogDescription: articleDescription,
        ogImage: articleImage,
        ogUrl: pageUrl,
        twitterTitle: articleTitle,
        twitterDescription: articleDescription,
        twitterImage: articleImage,
      }
    : undefined;

  useEffect(() => {
    if (loading || !articleTitle) return undefined;

    const animationFrame = window.requestAnimationFrame(() => {
      markReady();
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [articleTitle, loading, markReady]);

  return (
    <div className="blog-details-container">
      <HeaderMeta
        type="blog"
        overrides={metaOverrides}
        jsonLd={
          articleTitle
            ? [
                buildArticleSchema({
                  title: articleTitle,
                  description: articleDescription,
                  image: articleImage,
                  author: articleAuthor,
                  datePublished: articlePublishedAt,
                  dateModified: articleModifiedAt,
                  url: pageUrl,
                }),
                buildBreadcrumbSchema([
                  { name: 'Home', url: SITE_URL },
                  { name: 'Blog', url: `${SITE_URL}/blog` },
                  { name: articleTitle, url: pageUrl },
                ]),
              ]
            : []
        }
      />
      {loading ? (
        <Loader />
      ) : (
        <>
          {articleContent ? (
            <>
              <div className="blog-content">
                <div className="w1100 content">
                  <button
                    className="bg-image back-img"
                    onClick={() => {
                      props.history.push('/blog');
                    }}
                    aria-label="Back to blog list"
                  ></button>
                  <p className="date">
                    {dayjs(new Date(articlePublishedAt)).format('MMM DD, YYYY')}
                    <span className="author">{articleAuthor}</span>
                  </p>
                  <h1 className="title">{articleTitle}</h1>
                  <div
                    className="blog-details"
                    dangerouslySetInnerHTML={{ __html: articleContent }}
                    id="contentDetail"
                  />
                  <div className="blog-details">
                    <BlogAction />

                    <Separator />
                    <p>
                      Your next private jet charter flight starts with Airacer. Our team is ready
                      24/7 to help with every detail.
                    </p>
                    <p>
                      Frequent flyer? Discover our <Link to="/membership">Membership Program</Link>,
                      designed to give you exclusive perks like a dedicated concierge, exclusive
                      discounts, and priority access to empty leg flights.
                    </p>
                  </div>

                  <div className="blog-details-bottom">
                    {details.Tags && (
                      <ul className="article-tag">
                        {details.Tags.map((item) => {
                          return (
                            <li key={item.Name}>
                              <Link
                                to={{
                                  pathname: `/blog`,
                                  search: `?category=${item.Url}`,
                                }}
                              >
                                {item.Name}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    <div className="article-share">
                      <ul>
                        {shareIcons.map((share) => {
                          return (
                            <li
                              key={share.name}
                              className={share.className}
                              name={share.name}
                              onClick={shareHandle}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  shareHandle(e);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              aria-label={`Share on ${share.name}`}
                            />
                          );
                        })}
                      </ul>
                      <span>{details.PageViews} views</span>
                    </div>

                    <div className={`link-popup-box ${open && 'active'}`}>
                      <div className="popup-mask"></div>
                      <div className="link-popup">
                        <div className="public-title">
                          <img src="/imgs/svg/form-tit.svg" alt="title icon" />
                          <span>Share Link</span>
                        </div>
                        <div className="input-box">
                          <Input
                            value={linkValue}
                            readOnly="readonly"
                            ref={inputRef}
                            id="link-input"
                            onClick={() => {
                              inputRef.current.select();
                            }}
                          />
                        </div>
                        <div className="share-btn">
                          <Button onClick={closeShareLinkHandle}>Cancel</Button>
                          <Button onClick={copyLinkHandle}>Copy Link</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {recommendList && (
                <div className="blog-relevant-recommend">
                  <div className="title">
                    <span>Recommended Posts</span>
                    <a href="/blog">
                      <span>See All</span>
                    </a>
                  </div>
                  <ul className="blog-relevant">
                    {recommendList.map((item) => {
                      return (
                        <li key={item.blogId}>
                          <Link to={encodeURIComponent(`${item.url || item.blogId}`)}>
                            <img
                              src={`${process.env.REACT_APP_RESOURCE_URL}/${item.image}`}
                              alt="blog propaganda"
                            />
                            <div className="blog-text">
                              <h3 className="title">
                                <span>{item.title}</span>
                              </h3>
                              <p className="describe">{item.describe}</p>
                              <p className="date">
                                {dayjs(new Date(item.date)).format('MMM DD, YYYY')}
                              </p>
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <div className="not-found w1100">
              <img
                src={require('../../assets/imgs/webp/blog-noData.webp').default}
                alt="nodata img"
              />
              <p>The blog was not found</p>
            </div>
          )}
        </>
      )}
      <Footer></Footer>
    </div>
  );
};

export default BlogDetails;
